import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parse, startOfDay, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  BedDouble,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Home,
  Wifi,
  Car,
  Waves,
  Dumbbell,
  UtensilsCrossed,
  Wind,
  Sparkles,
  Star,
  Building2,
  TreePine,
  Castle,
  Warehouse,
  Mountain,
  X,
  SlidersHorizontal,
  Calendar as CalendarIcon,
  Loader2,
} from "lucide-react";

interface MarketplaceApartment {
  id: number;
  name: string;
  slug: string;
  location?: string;
  bedrooms?: number;
  maxGuests?: number;
  heroImage?: string;
  marketplaceDescription?: string;
  marketplacePrice?: string;
  marketplaceFeatured?: boolean;
  propertyType?: string;
  amenities?: string;
  organizationName?: string;
  organizationSlug?: string;
  photos?: Array<{ id: number; url: string; caption?: string; sortOrder: number; isCover: boolean }>;
}

interface MarketplaceDetail extends MarketplaceApartment {
  sections?: Array<{ id: number; title: string; content: string; category: string }>;
  bookingPageSlug?: string | null;
}

const AMENITY_CONFIG: Record<string, { label: string; icon: typeof Wifi }> = {
  wifi: { label: "WiFi", icon: Wifi },
  pool: { label: "Pool", icon: Waves },
  gym: { label: "Gym", icon: Dumbbell },
  kitchen: { label: "Kitchen", icon: UtensilsCrossed },
  parking: { label: "Parking", icon: Car },
  ac: { label: "A/C", icon: Wind },
  washer: { label: "Washer", icon: Wind },
  "ocean-view": { label: "Ocean View", icon: Waves },
  "city-view": { label: "City View", icon: Building2 },
  "bay-view": { label: "Bay View", icon: Waves },
  elevator: { label: "Elevator", icon: Building2 },
  concierge: { label: "Concierge", icon: Star },
};

const PROPERTY_TYPES = [
  { key: "all", label: "All", icon: Home },
  { key: "apartment", label: "Apartments", icon: Building2 },
  { key: "house", label: "Houses", icon: Home },
  { key: "villa", label: "Villas", icon: Castle },
  { key: "studio", label: "Studios", icon: Warehouse },
  { key: "penthouse", label: "Penthouses", icon: Mountain },
  { key: "loft", label: "Lofts", icon: Warehouse },
  { key: "cabin", label: "Cabins", icon: TreePine },
  { key: "condo", label: "Condos", icon: Building2 },
];

function formatPrice(price?: string | number) {
  if (!price) return null;
  const num = typeof price === "string" ? parseFloat(price) : price;
  if (isNaN(num)) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
}

function parseAmenities(amenities?: string): string[] {
  if (!amenities) return [];
  return amenities.split(",").map(a => a.trim()).filter(Boolean);
}

function getMinDate() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}

function getDefaultCheckout(checkIn: string) {
  const d = new Date(checkIn);
  d.setDate(d.getDate() + 2);
  return d.toISOString().split("T")[0];
}

interface AvailabilityResult {
  available: boolean;
  message?: string;
  nights?: number;
  totalNightly?: string;
  cleaningFee?: string;
  taxRate?: string;
  taxName?: string;
  taxAmount?: string;
  totalAmount?: string;
  currency?: string;
  ratePlanId?: number;
  organizationSlug?: string;
  bookingPageSlug?: string;
}

function ApartmentCardSkeleton() {
  return (
    <div className="overflow-visible">
      <Skeleton className="aspect-[4/3] w-full rounded-md" />
      <div className="pt-3">
        <Skeleton className="h-4 w-3/4 mb-2" />
        <Skeleton className="h-3 w-1/2 mb-2" />
        <Skeleton className="h-4 w-1/3 mt-2" />
      </div>
    </div>
  );
}

function PhotoCarousel({
  photos,
  heroImage,
  altText,
  testIdPrefix,
  featured,
}: {
  photos?: MarketplaceApartment["photos"];
  heroImage?: string;
  altText: string;
  testIdPrefix: string;
  featured?: boolean;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const allImages = useMemo(() => {
    if (photos && photos.length > 0) {
      const sorted = [...photos].sort((a, b) => {
        if (a.isCover && !b.isCover) return -1;
        if (!a.isCover && b.isCover) return 1;
        return a.sortOrder - b.sortOrder;
      });
      return sorted.map((p) => p.url);
    }
    if (heroImage) return [heroImage];
    return [];
  }, [photos, heroImage]);

  if (allImages.length === 0) {
    return (
      <div className="aspect-[4/3] bg-muted rounded-md flex items-center justify-center">
        <Home className="w-10 h-10 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-md group" data-testid={`${testIdPrefix}-carousel`}>
      <img
        src={allImages[currentIndex]}
        alt={altText}
        className="w-full h-full object-cover transition-transform duration-300"
        data-testid={`${testIdPrefix}-image`}
      />
      {featured && (
        <div className="absolute top-3 left-3">
          <Badge className="bg-white dark:bg-card text-foreground border-transparent shadow-sm gap-1">
            <Sparkles className="w-3 h-3 text-primary" />
            Featured
          </Badge>
        </div>
      )}
      {allImages.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 dark:bg-card/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentIndex((prev) => (prev + 1) % allImages.length);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 dark:bg-card/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
            {allImages.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIndex ? "bg-white" : "bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MarketplaceHeader({ onBack, transparent }: { onBack?: () => void; transparent?: boolean }) {
  const [, navigate] = useLocation();
  return (
    <header
      className={`sticky top-0 z-50 transition-colors ${transparent ? "bg-transparent" : "bg-background/95 backdrop-blur-sm border-b"}`}
      data-testid="marketplace-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3 flex-wrap">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <button
          onClick={() => navigate("/marketplace")}
          className="flex items-center gap-2"
          data-testid="link-marketplace-home"
        >
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Staio
          </span>
        </button>
      </div>
    </header>
  );
}

function MarketplaceFooter() {
  const [, navigate] = useLocation();
  return (
    <footer className="border-t mt-16 bg-muted/30" data-testid="marketplace-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
                <Home className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Staio</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Find your perfect stay from trusted hosts worldwide.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-3">For Guests</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="/marketplace" onClick={(e) => { e.preventDefault(); navigate("/marketplace"); }} className="hover:text-foreground cursor-pointer" data-testid="footer-link-browse">Browse Properties</a></li>
              <li><a href="/marketplace?featured=true" onClick={(e) => { e.preventDefault(); navigate("/marketplace?featured=true"); }} className="hover:text-foreground cursor-pointer" data-testid="footer-link-featured">Featured Listings</a></li>
              <li><a href="/about" onClick={(e) => { e.preventDefault(); navigate("/about"); }} className="hover:text-foreground cursor-pointer" data-testid="footer-link-about">About Staio</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-3">For Hosts</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="/for-hosts" onClick={(e) => { e.preventDefault(); navigate("/for-hosts"); }} className="hover:text-foreground cursor-pointer" data-testid="footer-link-list-property">List Your Property</a></li>
              <li><a href="/for-hosts" onClick={(e) => { e.preventDefault(); navigate("/for-hosts"); }} className="hover:text-foreground cursor-pointer" data-testid="footer-link-promote">Promote Listings</a></li>
              <li><a href="/for-hosts" onClick={(e) => { e.preventDefault(); navigate("/for-hosts"); }} className="hover:text-foreground cursor-pointer" data-testid="footer-link-management">Property Management</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t text-center text-xs text-muted-foreground">
          Powered by Staio
        </div>
      </div>
    </footer>
  );
}

function ApartmentCard({ apartment, checkIn, checkOut, testIdPrefix = "card" }: { apartment: MarketplaceApartment; checkIn?: string; checkOut?: string; testIdPrefix?: string }) {
  const [, navigate] = useLocation();
  const price = formatPrice(apartment.marketplacePrice);
  const amenities = parseAmenities(apartment.amenities).slice(0, 3);
  const dateParams = checkIn && checkOut ? `?checkIn=${checkIn}&checkOut=${checkOut}` : "";

  return (
    <div
      className="cursor-pointer group/card"
      onClick={() => navigate(`/marketplace/${apartment.slug}${dateParams}`)}
      data-testid={`${testIdPrefix}-apartment-${apartment.id}`}
    >
      <PhotoCarousel
        photos={apartment.photos}
        heroImage={apartment.heroImage}
        altText={apartment.name}
        testIdPrefix={`apartment-${apartment.id}`}
        featured={apartment.marketplaceFeatured}
      />
      <div className="pt-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-[15px] line-clamp-1 group-hover/card:text-primary transition-colors" data-testid={`text-name-${apartment.id}`}>
            {apartment.name}
          </h3>
        </div>
        {apartment.location && (
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5" data-testid={`text-location-${apartment.id}`}>
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="line-clamp-1">{apartment.location}</span>
          </p>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
          {apartment.bedrooms != null && (
            <span className="flex items-center gap-1" data-testid={`text-bedrooms-${apartment.id}`}>
              <BedDouble className="w-3 h-3" />
              {apartment.bedrooms === 0 ? "Studio" : `${apartment.bedrooms} bed${apartment.bedrooms !== 1 ? "s" : ""}`}
            </span>
          )}
          {apartment.maxGuests != null && (
            <>
              <span className="text-muted-foreground/40">|</span>
              <span className="flex items-center gap-1" data-testid={`text-guests-${apartment.id}`}>
                <Users className="w-3 h-3" />
                {apartment.maxGuests} guest{apartment.maxGuests !== 1 ? "s" : ""}
              </span>
            </>
          )}
          {amenities.length > 0 && (
            <>
              <span className="text-muted-foreground/40">|</span>
              {amenities.map(a => {
                const config = AMENITY_CONFIG[a];
                return config ? (
                  <span key={a} className="flex items-center gap-0.5">
                    <config.icon className="w-3 h-3" />
                  </span>
                ) : null;
              })}
            </>
          )}
        </div>
        {price && (
          <p className="mt-2 text-[15px]" data-testid={`text-price-${apartment.id}`}>
            <span className="font-semibold">{price}</span>
            <span className="text-sm text-muted-foreground"> / night</span>
          </p>
        )}
      </div>
    </div>
  );
}

function ListingView() {
  const [locationFilter, setLocationFilter] = useState("");
  const [bedroomsFilter, setBedroomsFilter] = useState("any");
  const [guestsFilter, setGuestsFilter] = useState("any");
  const [priceMax, setPriceMax] = useState("");
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [heroCheckIn, setHeroCheckIn] = useState("");
  const [heroCheckOut, setHeroCheckOut] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    location: "",
    bedrooms: "any",
    guests: "any",
    priceMax: "",
    propertyType: "all",
  });

  const { data: apartments, isLoading } = useQuery<MarketplaceApartment[]>({
    queryKey: ["/api/marketplace/apartments"],
  });

  const featuredApartments = useMemo(() => {
    if (!apartments) return [];
    return apartments.filter(a => a.marketplaceFeatured);
  }, [apartments]);

  const filteredApartments = useMemo(() => {
    if (!apartments) return [];
    return apartments.filter((apt) => {
      if (activeFilters.location) {
        const loc = (apt.location || "").toLowerCase();
        const name = apt.name.toLowerCase();
        const q = activeFilters.location.toLowerCase();
        if (!loc.includes(q) && !name.includes(q)) return false;
      }
      if (activeFilters.bedrooms !== "any") {
        const beds = parseInt(activeFilters.bedrooms);
        if (beds === 5) {
          if ((apt.bedrooms || 0) < 5) return false;
        } else {
          if (apt.bedrooms !== beds) return false;
        }
      }
      if (activeFilters.guests !== "any") {
        const g = parseInt(activeFilters.guests);
        if ((apt.maxGuests || 0) < g) return false;
      }
      if (activeFilters.priceMax) {
        const max = parseFloat(activeFilters.priceMax);
        const price = parseFloat(apt.marketplacePrice || "0");
        if (price > max) return false;
      }
      if (activeFilters.propertyType !== "all") {
        if ((apt.propertyType || "apartment") !== activeFilters.propertyType) return false;
      }
      return true;
    });
  }, [apartments, activeFilters]);

  const handleSearch = () => {
    setActiveFilters({
      location: locationFilter,
      bedrooms: bedroomsFilter,
      guests: guestsFilter,
      priceMax,
      propertyType: propertyTypeFilter,
    });
  };

  const hasActiveFilters = activeFilters.location || activeFilters.bedrooms !== "any" || activeFilters.guests !== "any" || activeFilters.priceMax || activeFilters.propertyType !== "all";

  const clearFilters = () => {
    setLocationFilter("");
    setBedroomsFilter("any");
    setGuestsFilter("any");
    setPriceMax("");
    setPropertyTypeFilter("all");
    setActiveFilters({
      location: "",
      bedrooms: "any",
      guests: "any",
      priceMax: "",
      propertyType: "all",
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      sessionStorage.setItem("staio_ref", ref);
    }
  }, []);

  return (
    <>
      <MarketplaceHeader />

      {/* Hero Search Section */}
      <div className="bg-gradient-to-br from-primary/8 via-background to-primary/4 dark:from-primary/5 dark:via-background dark:to-primary/3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 sm:pt-12 sm:pb-8">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2 tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-marketplace-title"
          >
            Find your perfect stay
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg mb-6 max-w-xl" data-testid="text-marketplace-subtitle">
            Browse unique apartments and vacation rentals from trusted hosts
          </p>

          {/* Search Bar */}
          <div className="flex flex-col gap-2 max-w-4xl">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search by location or property name..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="pl-10 bg-background"
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="input-location"
                />
              </div>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[170px] justify-start text-left font-normal bg-background truncate",
                        !heroCheckIn && "text-muted-foreground"
                      )}
                      data-testid="input-checkin"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      {heroCheckIn ? format(parse(heroCheckIn, "yyyy-MM-dd", new Date()), "MMM d") : "Check-in"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={heroCheckIn ? parse(heroCheckIn, "yyyy-MM-dd", new Date()) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const val = format(date, "yyyy-MM-dd");
                          setHeroCheckIn(val);
                          if (!heroCheckOut || val >= heroCheckOut) {
                            setHeroCheckOut(getDefaultCheckout(val));
                          }
                        }
                      }}
                      disabled={(date) => date < startOfDay(new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[170px] justify-start text-left font-normal bg-background truncate",
                        !heroCheckOut && "text-muted-foreground"
                      )}
                      data-testid="input-checkout"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      {heroCheckOut ? format(parse(heroCheckOut, "yyyy-MM-dd", new Date()), "MMM d") : "Check-out"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={heroCheckOut ? parse(heroCheckOut, "yyyy-MM-dd", new Date()) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          setHeroCheckOut(format(date, "yyyy-MM-dd"));
                        }
                      }}
                      disabled={(date) => {
                        const minDate = heroCheckIn ? addDays(parse(heroCheckIn, "yyyy-MM-dd", new Date()), 1) : startOfDay(new Date());
                        return date < minDate;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="button-toggle-filters"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 w-2 h-2 rounded-full bg-primary" />
                )}
              </Button>
              <Button onClick={handleSearch} data-testid="button-search">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              {heroCheckIn && heroCheckOut && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  <CalendarIcon className="w-3 h-3" />
                  {heroCheckIn} → {heroCheckOut}
                </Badge>
              )}
            </div>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 max-w-3xl" data-testid="filter-panel">
              <Card className="overflow-visible">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Bedrooms</label>
                      <Select value={bedroomsFilter} onValueChange={setBedroomsFilter}>
                        <SelectTrigger data-testid="select-bedrooms">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="0">Studio</SelectItem>
                          <SelectItem value="1">1 Bedroom</SelectItem>
                          <SelectItem value="2">2 Bedrooms</SelectItem>
                          <SelectItem value="3">3 Bedrooms</SelectItem>
                          <SelectItem value="4">4 Bedrooms</SelectItem>
                          <SelectItem value="5">5+ Bedrooms</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Guests</label>
                      <Select value={guestsFilter} onValueChange={setGuestsFilter}>
                        <SelectTrigger data-testid="select-guests">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="1">1+ Guest</SelectItem>
                          <SelectItem value="2">2+ Guests</SelectItem>
                          <SelectItem value="3">3+ Guests</SelectItem>
                          <SelectItem value="4">4+ Guests</SelectItem>
                          <SelectItem value="6">6+ Guests</SelectItem>
                          <SelectItem value="8">8+ Guests</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Max Price / night</label>
                      <Input
                        type="number"
                        placeholder="No limit"
                        value={priceMax}
                        onChange={(e) => setPriceMax(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        data-testid="input-price-max"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                    <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-filters">
                      <X className="w-3 h-3 mr-1" /> Clear all
                    </Button>
                    <Button size="sm" onClick={() => { handleSearch(); setShowFilters(false); }} data-testid="button-apply-filters">
                      Apply Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Property Type Categories */}
      <div className="border-b bg-background sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 no-scrollbar">
            {PROPERTY_TYPES.map((type) => {
              const isActive = propertyTypeFilter === type.key;
              return (
                <button
                  key={type.key}
                  onClick={() => {
                    setPropertyTypeFilter(type.key);
                    setActiveFilters(prev => ({ ...prev, propertyType: type.key }));
                  }}
                  className={`flex flex-col items-center gap-1 px-4 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    isActive
                      ? "text-primary border-b-2 border-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`button-type-${type.key}`}
                >
                  <type.icon className="w-5 h-5" />
                  <span>{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Section */}
        {!hasActiveFilters && featuredApartments.length > 0 && (
          <section className="mb-10" data-testid="featured-section">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Featured Properties</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="featured-grid">
              {featuredApartments.map((apt) => (
                <ApartmentCard key={`featured-${apt.id}`} apartment={apt} checkIn={heroCheckIn} checkOut={heroCheckOut} testIdPrefix="featured" />
              ))}
            </div>
          </section>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="active-filters">
            <span className="text-sm text-muted-foreground">Filters:</span>
            {activeFilters.location && (
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" /> {activeFilters.location}
                <button data-testid="button-clear-location" onClick={() => { setLocationFilter(""); setActiveFilters(prev => ({ ...prev, location: "" })); }}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {activeFilters.bedrooms !== "any" && (
              <Badge variant="secondary" className="gap-1">
                <BedDouble className="w-3 h-3" /> {activeFilters.bedrooms === "0" ? "Studio" : `${activeFilters.bedrooms} bed${activeFilters.bedrooms !== "1" ? "s" : ""}`}
                <button data-testid="button-clear-bedrooms" onClick={() => { setBedroomsFilter("any"); setActiveFilters(prev => ({ ...prev, bedrooms: "any" })); }}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {activeFilters.guests !== "any" && (
              <Badge variant="secondary" className="gap-1">
                <Users className="w-3 h-3" /> {activeFilters.guests}+ guests
                <button data-testid="button-clear-guests" onClick={() => { setGuestsFilter("any"); setActiveFilters(prev => ({ ...prev, guests: "any" })); }}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {activeFilters.priceMax && (
              <Badge variant="secondary" className="gap-1">
                Max ${activeFilters.priceMax}/night
                <button data-testid="button-clear-price" onClick={() => { setPriceMax(""); setActiveFilters(prev => ({ ...prev, priceMax: "" })); }}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {activeFilters.propertyType !== "all" && (
              <Badge variant="secondary" className="gap-1">
                {activeFilters.propertyType}
                <button data-testid="button-clear-type" onClick={() => { setPropertyTypeFilter("all"); setActiveFilters(prev => ({ ...prev, propertyType: "all" })); }}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-all">
              Clear all
            </Button>
          </div>
        )}

        {/* All Properties */}
        <section>
          {!hasActiveFilters && filteredApartments.length > 0 && (
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              All Properties
            </h2>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="marketplace-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <ApartmentCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredApartments.length === 0 ? (
            <div className="text-center py-20" data-testid="marketplace-empty">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold mb-2">No properties found</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Try adjusting your search or filters to discover more places to stay
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters} data-testid="button-clear-empty">
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4" data-testid="text-results-count">
                {filteredApartments.length} propert{filteredApartments.length !== 1 ? "ies" : "y"} available
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-8" data-testid="marketplace-grid">
                {filteredApartments.map((apt) => (
                  <ApartmentCard key={apt.id} apartment={apt} checkIn={heroCheckIn} checkOut={heroCheckOut} />
                ))}
              </div>
            </>
          )}
        </section>
      </main>

      <MarketplaceFooter />
    </>
  );
}

function DetailView({ slug }: { slug: string }) {
  const [, navigate] = useLocation();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const urlParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const initialCheckIn = urlParams.get("checkIn") || "";
  const initialCheckOut = urlParams.get("checkOut") || "";

  const [detailCheckIn, setDetailCheckIn] = useState(initialCheckIn);
  const [detailCheckOut, setDetailCheckOut] = useState(initialCheckOut);
  const [availability, setAvailability] = useState<AvailabilityResult | null>(null);

  const { data: apartment, isLoading, error } = useQuery<MarketplaceDetail>({
    queryKey: ["/api/marketplace/apartments", slug],
  });

  const availabilityMutation = useMutation({
    mutationFn: async (body: { apartmentId: number; checkIn: string; checkOut: string }) => {
      const res = await apiRequest("POST", "/api/marketplace/availability", body);
      return res.json();
    },
    onSuccess: (data: AvailabilityResult) => {
      setAvailability(data);
    },
  });

  useEffect(() => {
    if (apartment && detailCheckIn && detailCheckOut && detailCheckIn < detailCheckOut) {
      availabilityMutation.mutate({
        apartmentId: apartment.id,
        checkIn: detailCheckIn,
        checkOut: detailCheckOut,
      });
    } else {
      setAvailability(null);
    }
  }, [apartment?.id, detailCheckIn, detailCheckOut]);

  if (isLoading) {
    return (
      <>
        <MarketplaceHeader onBack={() => navigate("/marketplace")} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="detail-loading">
          <Skeleton className="h-8 w-2/3 mb-4" />
          <Skeleton className="aspect-[16/9] w-full rounded-md mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-3">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <Skeleton className="h-48 w-full rounded-md" />
          </div>
        </div>
      </>
    );
  }

  if (error || !apartment) {
    return (
      <>
        <MarketplaceHeader onBack={() => navigate("/marketplace")} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="detail-error">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Home className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Property not found</h2>
          <p className="text-muted-foreground mb-6">This property may no longer be available.</p>
          <Button variant="outline" onClick={() => navigate("/marketplace")} data-testid="button-back-to-marketplace">
            Back to Marketplace
          </Button>
        </div>
      </>
    );
  }

  const allPhotos = (() => {
    if (apartment.photos && apartment.photos.length > 0) {
      const sorted = [...apartment.photos].sort((a, b) => {
        if (a.isCover && !b.isCover) return -1;
        if (!a.isCover && b.isCover) return 1;
        return a.sortOrder - b.sortOrder;
      });
      return sorted.map((p) => p.url);
    }
    if (apartment.heroImage) return [apartment.heroImage];
    return [];
  })();

  const price = formatPrice(apartment.marketplacePrice);
  const amenities = parseAmenities(apartment.amenities);

  return (
    <>
      <MarketplaceHeader onBack={() => navigate("/marketplace")} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Title Section */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {apartment.marketplaceFeatured && (
              <Badge className="bg-primary/10 text-primary border-transparent gap-1">
                <Sparkles className="w-3 h-3" /> Featured
              </Badge>
            )}
            {apartment.propertyType && apartment.propertyType !== "apartment" && (
              <Badge variant="secondary" className="capitalize">{apartment.propertyType}</Badge>
            )}
          </div>
          <h1
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="text-detail-name"
          >
            {apartment.name}
          </h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1 flex-wrap">
            {apartment.location && (
              <span className="flex items-center gap-1" data-testid="text-detail-location">
                <MapPin className="w-4 h-4" />
                {apartment.location}
              </span>
            )}
            {apartment.organizationName && (
              <span data-testid="text-detail-org">
                Hosted by <span className="font-medium text-foreground">{apartment.organizationName}</span>
              </span>
            )}
          </div>
        </div>

        {/* Photo Gallery */}
        {allPhotos.length > 0 ? (
          <div className="relative aspect-[16/9] overflow-hidden rounded-md mb-6 group" data-testid="detail-gallery">
            <img
              src={allPhotos[currentPhotoIndex]}
              alt={apartment.name}
              className="w-full h-full object-cover"
              data-testid="detail-gallery-image"
            />
            {allPhotos.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 dark:bg-card/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  data-testid="detail-gallery-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 dark:bg-card/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  data-testid="detail-gallery-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 right-3">
                  <Badge variant="secondary" className="bg-black/60 text-white border-transparent text-xs">
                    {currentPhotoIndex + 1} / {allPhotos.length}
                  </Badge>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="aspect-[16/9] bg-muted rounded-md mb-6 flex items-center justify-center">
            <Home className="w-16 h-16 text-muted-foreground" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Quick Info */}
            <div className="flex items-center gap-6 pb-6 border-b flex-wrap">
              {apartment.bedrooms != null && (
                <div className="flex items-center gap-2" data-testid="text-detail-bedrooms">
                  <div className="p-2 rounded-md bg-muted">
                    <BedDouble className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{apartment.bedrooms === 0 ? "Studio" : `${apartment.bedrooms} bedroom${apartment.bedrooms !== 1 ? "s" : ""}`}</p>
                  </div>
                </div>
              )}
              {apartment.maxGuests != null && (
                <div className="flex items-center gap-2" data-testid="text-detail-guests">
                  <div className="p-2 rounded-md bg-muted">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Up to {apartment.maxGuests} guest{apartment.maxGuests !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {apartment.marketplaceDescription && (
              <div data-testid="text-detail-description">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>About this place</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {apartment.marketplaceDescription}
                </p>
              </div>
            )}

            {/* Amenities */}
            {amenities.length > 0 && (
              <div data-testid="detail-amenities">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>What this place offers</h2>
                <div className="grid grid-cols-2 gap-3">
                  {amenities.map((a) => {
                    const config = AMENITY_CONFIG[a];
                    if (!config) return (
                      <div key={a} className="flex items-center gap-3 py-2 text-sm capitalize">
                        <Star className="w-4 h-4 text-muted-foreground" />
                        <span>{a.replace(/-/g, " ")}</span>
                      </div>
                    );
                    return (
                      <div key={a} className="flex items-center gap-3 py-2 text-sm" data-testid={`amenity-${a}`}>
                        <config.icon className="w-4 h-4 text-muted-foreground" />
                        <span>{config.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Content Sections */}
            {apartment.sections && apartment.sections.length > 0 && (
              <div className="space-y-6 pt-6 border-t" data-testid="detail-sections">
                {apartment.sections.map((section) => (
                  <div key={section.id} data-testid={`detail-section-${section.id}`}>
                    <h3 className="text-base font-semibold mb-2">{section.title}</h3>
                    <div
                      className="text-sm text-muted-foreground leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Booking Sidebar */}
          <div>
            <Card className="sticky top-20 overflow-visible">
              <CardContent className="p-5">
                {price && (
                  <div className="mb-4" data-testid="text-detail-price">
                    <span className="text-2xl font-bold">{price}</span>
                    <span className="text-muted-foreground"> / night</span>
                  </div>
                )}

                <div className="space-y-3 mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Check-in</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal text-sm truncate",
                              !detailCheckIn && "text-muted-foreground"
                            )}
                            data-testid="input-detail-checkin"
                          >
                            <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                            {detailCheckIn ? format(parse(detailCheckIn, "yyyy-MM-dd", new Date()), "MMM d") : "Select"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={detailCheckIn ? parse(detailCheckIn, "yyyy-MM-dd", new Date()) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                const val = format(date, "yyyy-MM-dd");
                                setDetailCheckIn(val);
                                if (!detailCheckOut || val >= detailCheckOut) {
                                  setDetailCheckOut(getDefaultCheckout(val));
                                }
                              }
                            }}
                            disabled={(date) => date < startOfDay(new Date())}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Check-out</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal text-sm truncate",
                              !detailCheckOut && "text-muted-foreground"
                            )}
                            data-testid="input-detail-checkout"
                          >
                            <CalendarIcon className="mr-2 h-3.5 w-3.5 shrink-0" />
                            {detailCheckOut ? format(parse(detailCheckOut, "yyyy-MM-dd", new Date()), "MMM d") : "Select"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={detailCheckOut ? parse(detailCheckOut, "yyyy-MM-dd", new Date()) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setDetailCheckOut(format(date, "yyyy-MM-dd"));
                              }
                            }}
                            disabled={(date) => {
                              const minDate = detailCheckIn ? addDays(parse(detailCheckIn, "yyyy-MM-dd", new Date()), 1) : startOfDay(new Date());
                              return date < minDate;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                {availabilityMutation.isPending && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3" data-testid="availability-loading">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking availability...
                  </div>
                )}

                {availability && !availabilityMutation.isPending && (
                  <div className="mb-4" data-testid="availability-result">
                    {availability.available ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">{availability.nights} night{availability.nights !== 1 ? "s" : ""}</span>
                          <span>{formatPrice(availability.totalNightly)}</span>
                        </div>
                        {parseFloat(availability.cleaningFee || "0") > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Cleaning fee</span>
                            <span>{formatPrice(availability.cleaningFee)}</span>
                          </div>
                        )}
                        {parseFloat(availability.taxAmount || "0") > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{availability.taxName}</span>
                            <span>{formatPrice(availability.taxAmount)}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between font-semibold pt-2 border-t">
                          <span>Total</span>
                          <span>{formatPrice(availability.totalAmount)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-destructive" data-testid="text-unavailable">
                        {availability.message || "Not available for selected dates"}
                      </p>
                    )}
                  </div>
                )}

                {availability?.available && availability.organizationSlug && availability.bookingPageSlug ? (
                  <Button
                    className="w-full"
                    onClick={() => {
                      const bookUrl = `/book/${availability.organizationSlug}/${availability.bookingPageSlug}?checkIn=${detailCheckIn}&checkOut=${detailCheckOut}&apartmentId=${apartment.id}&ratePlanId=${availability.ratePlanId}&source=marketplace`;
                      const ref = typeof window !== "undefined" ? sessionStorage.getItem("staio_ref") : null;
                      navigate(ref ? `${bookUrl}&ref=${ref}` : bookUrl);
                    }}
                    data-testid="button-book-now"
                  >
                    Reserve · {formatPrice(availability.totalAmount)}
                  </Button>
                ) : !detailCheckIn || !detailCheckOut ? (
                  <Button className="w-full" variant="outline" disabled data-testid="button-book-now-disabled">
                    Select dates to book
                  </Button>
                ) : availability && !availability.available ? (
                  <Button className="w-full" disabled data-testid="button-book-now-disabled">
                    Not Available
                  </Button>
                ) : (
                  <Button className="w-full" disabled={availabilityMutation.isPending} data-testid="button-book-now-disabled">
                    {availabilityMutation.isPending ? "Checking..." : "Select dates to book"}
                  </Button>
                )}
                <p className="text-xs text-center text-muted-foreground mt-3">
                  You won't be charged yet
                </p>

                <div className="space-y-2 text-sm text-muted-foreground mt-4 pt-4 border-t">
                  {apartment.bedrooms != null && (
                    <div className="flex items-center gap-2">
                      <BedDouble className="w-4 h-4" />
                      <span>{apartment.bedrooms === 0 ? "Studio" : `${apartment.bedrooms} bedroom${apartment.bedrooms !== 1 ? "s" : ""}`}</span>
                    </div>
                  )}
                  {apartment.maxGuests != null && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Up to {apartment.maxGuests} guest{apartment.maxGuests !== 1 ? "s" : ""}</span>
                    </div>
                  )}
                  {apartment.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{apartment.location}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <MarketplaceFooter />
    </>
  );
}

export default function MarketplacePage() {
  const [matchDetail, params] = useRoute("/marketplace/:slug");

  if (matchDetail && params?.slug) {
    return <DetailView slug={params.slug} />;
  }

  return <ListingView />;
}
